import { createServerFn } from "@tanstack/react-start";

// Read-only desk intelligence for the command center. Derived entirely from
// stored M5 price bars. No engine parameters, thresholds or policy values are
// exposed: only structure labels, liquidity levels and range statistics that
// any chart reader could derive independently.

export type StructureLabel =
  | "Bullish structure"
  | "Bearish structure"
  | "Bullish pullback"
  | "Bearish pullback"
  | "Consolidating"
  | "Insufficient data";

export interface StructureRow {
  tf: "D1" | "H4" | "H1" | "M15";
  label: StructureLabel;
  bias: "bull" | "bear" | "flat";
}

export interface FvgZone {
  kind: "bullish" | "bearish";
  top: number;
  bottom: number;
  /** Distance in USD from the live reference price to the near edge. */
  distance: number;
}

export interface DeskReading {
  /** One line on the current liquidity balance. */
  state: string;
  bias: "bull" | "bear" | "flat";
  /** Condition that would qualify long interest. */
  long: string;
  /** Condition that would qualify short interest. */
  short: string;
  /** Operational caution for the current tape. */
  warning: string;
}

export interface DeskIntel {
  price: number | null;
  reading: DeskReading;
  structure: StructureRow[];
  fvgAbove: FvgZone | null;
  fvgBelow: FvgZone | null;
  adr: {
    average: number | null;
    todayRange: number | null;
    consumedPct: number | null;
    todayHigh: number | null;
    todayLow: number | null;
    days: number;
  };
  liquidity: {
    asianHigh: number | null;
    asianLow: number | null;
    asianHighSwept: boolean;
    asianLowSwept: boolean;
    prevDayHigh: number | null;
    prevDayLow: number | null;
    prevDayHighSwept: boolean;
    prevDayLowSwept: boolean;
  };
}

type Bar = { t: number; o: number; h: number; l: number; c: number };

function bucket(bars: Bar[], step: number): Bar[] {
  const out: Bar[] = [];
  for (const b of bars) {
    const k = Math.floor(b.t / step) * step;
    const last = out[out.length - 1];
    if (last && last.t === k) {
      last.h = Math.max(last.h, b.h);
      last.l = Math.min(last.l, b.l);
      last.c = b.c;
    } else out.push({ t: k, o: b.o, h: b.h, l: b.l, c: b.c });
  }
  return out;
}

/** Fractal swing points with a shoulder of `k` bars on each side (two by default). */
function swings(bars: Bar[], k = 2) {
  const highs: number[] = [];
  const lows: number[] = [];
  for (let i = k; i < bars.length - k; i++) {
    const b = bars[i]!;
    const win = bars.slice(i - k, i + k + 1);
    if (win.every((x) => x === b || x.h <= b.h)) highs.push(b.h);
    if (win.every((x) => x === b || x.l >= b.l)) lows.push(b.l);
  }
  return { highs, lows };
}

// Weekly bars use a one-bar shoulder: two-bar weekly fractals rarely confirm on a trending month.
export function classifyStructure(bars: Bar[], shoulder = 2): { label: StructureLabel; bias: "bull" | "bear" | "flat" } {
  if (bars.length < 12) return { label: "Insufficient data", bias: "flat" };
  const { highs, lows } = swings(bars, shoulder);
  if (highs.length < 2 || lows.length < 2) return { label: "Consolidating", bias: "flat" };
  const hh = highs.at(-1)! > highs.at(-2)!;
  const hl = lows.at(-1)! > lows.at(-2)!;
  const lh = highs.at(-1)! < highs.at(-2)!;
  const ll = lows.at(-1)! < lows.at(-2)!;
  const close = bars.at(-1)!.c;
  // Trend reference: simple mean of up to the last 50 closes on this timeframe.
  const ref = bars.slice(-50);
  const sma = ref.reduce((s, b) => s + b.c, 0) / ref.length;
  const above = close > sma;
  const below = close < sma;

  // Break of structure first: a body close beyond the last confirmed swing
  // overrides the swing order, because the newest swing on a fast move cannot
  // confirm until two more candles print and would leave the old trend standing.
  if (close < lows.at(-1)!) return below ? { label: "Bearish structure", bias: "bear" } : { label: "Bearish pullback", bias: "bear" };
  if (close > highs.at(-1)!) return above ? { label: "Bullish structure", bias: "bull" } : { label: "Bullish pullback", bias: "bull" };
  // Intact swings only count while price sits on their side of the trend reference.
  if (hh && hl) return above ? { label: "Bullish structure", bias: "bull" } : { label: "Consolidating", bias: "flat" };
  if (lh && ll) return below ? { label: "Bearish structure", bias: "bear" } : { label: "Consolidating", bias: "flat" };
  // Mixed swings (lower high with higher low, or the reverse) are compression.
  // The side of the trend reference decides the lean, never the swing order.
  if ((lh && hl) || (hh && ll)) {
    if (below) return { label: "Bearish pullback", bias: "bear" };
    if (above) return { label: "Bullish pullback", bias: "bull" };
    return { label: "Consolidating", bias: "flat" };
  }
  // One side flat: lean with the progressing side only if price agrees.
  if ((hl || hh) && above) return { label: "Bullish pullback", bias: "bull" };
  if ((lh || ll) && below) return { label: "Bearish pullback", bias: "bear" };
  return { label: "Consolidating", bias: "flat" };
}

/** Three-candle imbalance. Returns the nearest unmitigated gap on each side. */
function nearestFvgs(bars: Bar[], price: number) {
  let above: FvgZone | null = null;
  let below: FvgZone | null = null;
  for (let i = 2; i < bars.length; i++) {
    const a = bars[i - 2]!;
    const c = bars[i]!;
    const later = bars.slice(i + 1);
    if (c.l > a.h) {
      const zone = { bottom: a.h, top: c.l };
      const filled = later.some((x) => x.l <= zone.bottom);
      if (!filled && zone.top < price) {
        const d = price - zone.top;
        if (!below || d < below.distance) below = { kind: "bullish", ...zone, distance: d };
      }
    }
    if (c.h < a.l) {
      const zone = { bottom: c.h, top: a.l };
      const filled = later.some((x) => x.h >= zone.top);
      if (!filled && zone.bottom > price) {
        const d = zone.bottom - price;
        if (!above || d < above.distance) above = { kind: "bearish", ...zone, distance: d };
      }
    }
  }
  return { above, below };
}

const usd = (n: number) => n.toFixed(2);

/**
 * Plain-language tactical reading. Derived only from the same public levels the
 * panels already display: swept liquidity, unmitigated imbalances, multi
 * timeframe structure and range consumption. No engine thresholds are exposed.
 */
function deriveReading(input: {
  price: number | null;
  structure: StructureRow[];
  fvgAbove: FvgZone | null;
  fvgBelow: FvgZone | null;
  consumedPct: number | null;
  liq: DeskIntel["liquidity"];
}): DeskReading {
  const { price, structure, fvgAbove, fvgBelow, consumedPct, liq } = input;
  if (price == null) {
    return {
      state: "Tape unavailable. Levels are not confirmed.",
      bias: "flat",
      long: "No qualified long reference while data is incomplete.",
      short: "No qualified short reference while data is incomplete.",
      warning: "Stand aside until the tape reconnects.",
    };
  }

  const sellSwept = liq.asianLowSwept || liq.prevDayLowSwept;
  const buySwept = liq.asianHighSwept || liq.prevDayHighSwept;

  const sellLevels = [
    liq.asianLowSwept && liq.asianLow != null ? `Asian low ${usd(liq.asianLow)}` : null,
    liq.prevDayLowSwept && liq.prevDayLow != null ? `previous day low ${usd(liq.prevDayLow)}` : null,
  ].filter(Boolean).join(" and ");
  const buyLevels = [
    liq.asianHighSwept && liq.asianHigh != null ? `Asian high ${usd(liq.asianHigh)}` : null,
    liq.prevDayHighSwept && liq.prevDayHigh != null ? `previous day high ${usd(liq.prevDayHigh)}` : null,
  ].filter(Boolean).join(" and ");

  let state: string;
  if (sellSwept && buySwept) {
    state = `Both sides have been taken (${sellLevels}; ${buyLevels}). Two way liquidity, low directional clarity.`;
  } else if (sellSwept) {
    state = `Sell side liquidity at the ${sellLevels} has been swept. Buy side above remains intact.`;
  } else if (buySwept) {
    state = `Buy side liquidity at the ${buyLevels} has been swept. Sell side below remains intact.`;
  } else {
    state = "No liquidity taken on either side. Price is still inside the prepared range.";
  }

  const htf = structure.filter((r) => r.tf === "D1" || r.tf === "H4");
  const bullCount = htf.filter((r) => r.bias === "bull").length;
  const bearCount = htf.filter((r) => r.bias === "bear").length;
  const bias: DeskReading["bias"] = bullCount > bearCount ? "bull" : bearCount > bullCount ? "bear" : "flat";

  // Reclaim reference is the highest swept sell side level.
  const sweptLows = [
    liq.asianLowSwept ? liq.asianLow : null,
    liq.prevDayLowSwept ? liq.prevDayLow : null,
  ].filter((v): v is number => v != null);
  const reclaim = sweptLows.length ? Math.max(...sweptLows) : null;
  const sweptHighs = [
    liq.asianHighSwept ? liq.asianHigh : null,
    liq.prevDayHighSwept ? liq.prevDayHigh : null,
  ].filter((v): v is number => v != null);
  const lose = sweptHighs.length ? Math.min(...sweptHighs) : null;

  const pullbackLong = fvgBelow
    ? `Long interest on a controlled pullback into the discount gap ${usd(fvgBelow.bottom)} to ${usd(fvgBelow.top)} with rejection.`
    : reclaim != null
      ? `Long interest on a controlled pullback that holds above ${usd(reclaim)}.`
      : "Long interest only on a controlled pullback with rejection.";

  const long = sellSwept
    ? reclaim != null && price > reclaim
      ? `Sweep reclaimed. Price holds above ${usd(reclaim)}. ${pullbackLong}`
      : reclaim != null
        ? `Reversal case. Long interest only if price reclaims ${usd(reclaim)} and closes an hourly candle back above it with intent.`
        : "Reversal case. Long interest only after price closes back inside the range with intent."
    : fvgBelow
      ? `Continuation case. ${pullbackLong}`
      : `Long interest requires a sweep of the lows near ${usd(liq.asianLow ?? liq.prevDayLow ?? price)} followed by a reclaim. No clean demand reference above it.`;

  const pullbackShort = fvgAbove
    ? `Short interest on a pullback into the premium gap ${usd(fvgAbove.bottom)} to ${usd(fvgAbove.top)} with rejection.`
    : lose != null
      ? `Short interest on a pullback that fails below ${usd(lose)}.`
      : "Short interest only on a pullback with rejection.";

  const short = buySwept
    ? lose != null && price < lose
      ? `Sweep rejected. Price holds below ${usd(lose)}. ${pullbackShort}`
      : lose != null
        ? `Reversal case. Short interest only if price fails back below ${usd(lose)} on an hourly close.`
        : "Reversal case. Short interest only after price fails back inside the range."
    : fvgAbove
      ? `Continuation case. ${pullbackShort}`
      : `Short interest requires a sweep of the highs near ${usd(liq.asianHigh ?? liq.prevDayHigh ?? price)} followed by a failure back below. No clean supply reference below it.`;


  let warning: string;
  if (consumedPct != null && consumedPct >= 150) {
    warning = `Daily range is at ${Math.round(consumedPct)} percent of its recent average. Late entries in the direction of the move carry poor reward.`;
  } else if (sellSwept && buySwept) {
    warning = "Both pools are gone. Expect chop until one side is defended. Reduce expectations for a clean trend.";
  } else if (sellSwept) {
    warning = reclaim != null && price > reclaim ? "The sweep has been reclaimed. Chasing the extension higher is the weak entry. Wait for a controlled pullback." : "Chasing the break lower after the sweep is the common trap. Wait for the reaction, not the break.";
  } else if (buySwept) {
    warning = lose != null && price < lose ? "The sweep has been rejected. Chasing the extension lower is the weak entry. Wait for a controlled pullback." : "Chasing the break higher after the sweep is the common trap. Wait for the reaction, not the break.";
  } else if (consumedPct != null && consumedPct < 35) {
    warning = "Range is compressed. Expansion has not started, so breakout attempts fail more often here.";
  } else {
    warning = "No liquidity event yet. Patience until a level is taken and defended.";
  }

  return { state, bias, long, short, warning };
}

export const getDeskIntel = createServerFn({ method: "GET" }).handler(async (): Promise<DeskIntel> => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const since = Math.floor(Date.now() / 1000) - 26 * 86400;
  // The Data API caps a single response at 1000 rows, so newest-first pages
  // are read until the window is covered.
  const PAGE = 1000;
  const collected: Bar[] = [];
  for (let page = 0; page < 9; page++) {
    const { data: rows, error } = await supabaseAdmin
      .from("otas_m5_bars")
      .select("t, o, h, l, c")
      .gte("t", since)
      .order("t", { ascending: false })
      .range(page * PAGE, page * PAGE + PAGE - 1);
    if (error) throw new Error("Desk intel read failed");
    if (!rows?.length) break;
    for (const r of rows) {
      collected.push({ t: Number(r.t), o: Number(r.o), h: Number(r.h), l: Number(r.l), c: Number(r.c) });
    }
    if (rows.length < PAGE) break;
  }
  const bars: Bar[] = collected.reverse();
  const price = bars.at(-1)?.c ?? null;

  const m15 = bucket(bars, 900);
  const h1 = bucket(bars, 3600);
  const h4 = bucket(bars, 14400);
  const d1 = bucket(bars, 86400);

  const structure: StructureRow[] = [
    { tf: "D1", ...classifyStructure(d1) },
    { tf: "H4", ...classifyStructure(h4.slice(-60)) },
    { tf: "H1", ...classifyStructure(h1.slice(-80)) },
    { tf: "M15", ...classifyStructure(m15.slice(-80)) },
  ];

  const fvg = price != null ? nearestFvgs(h1.slice(-120), price) : { above: null, below: null };

  // Average daily range over completed UTC days, today excluded.
  const todayKey = Math.floor((bars.at(-1)?.t ?? Date.now() / 1000) / 86400) * 86400;
  const complete = d1.filter((d) => d.t < todayKey).slice(-14);
  const average = complete.length ? complete.reduce((s, d) => s + (d.h - d.l), 0) / complete.length : null;
  const today = d1.find((d) => d.t === todayKey) ?? null;
  const todayRange = today ? today.h - today.l : null;
  const consumedPct = average && todayRange != null ? (todayRange / average) * 100 : null;

  // Asian session: 22:00 UTC of the previous calendar day to 07:00 UTC.
  const asianStart = todayKey - 2 * 3600;
  const asianEnd = todayKey + 7 * 3600;
  const asianBars = bars.filter((b) => b.t >= asianStart && b.t < asianEnd);
  const asianHigh = asianBars.length ? Math.max(...asianBars.map((b) => b.h)) : null;
  const asianLow = asianBars.length ? Math.min(...asianBars.map((b) => b.l)) : null;
  const afterAsia = bars.filter((b) => b.t >= asianEnd);

  const prev = d1.filter((d) => d.t < todayKey).at(-1) ?? null;
  const todayBars = bars.filter((b) => b.t >= todayKey);

  const liquidity = {
    asianHigh,
    asianLow,
    asianHighSwept: asianHigh != null && afterAsia.some((b) => b.h > asianHigh),
    asianLowSwept: asianLow != null && afterAsia.some((b) => b.l < asianLow),
    prevDayHigh: prev?.h ?? null,
    prevDayLow: prev?.l ?? null,
    prevDayHighSwept: prev != null && todayBars.some((b) => b.h > prev.h),
    prevDayLowSwept: prev != null && todayBars.some((b) => b.l < prev.l),
  };

  return {
    price,
    reading: deriveReading({
      price,
      structure,
      fvgAbove: fvg.above,
      fvgBelow: fvg.below,
      consumedPct,
      liq: liquidity,
    }),
    structure,
    fvgAbove: fvg.above,
    fvgBelow: fvg.below,
    adr: {
      average,
      todayRange,
      consumedPct,
      todayHigh: today?.h ?? null,
      todayLow: today?.l ?? null,
      days: complete.length,
    },
    liquidity,
  };
});
